import { Component, signal, inject, computed, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { StageComponent, CoreShapeComponent } from 'ng2-konva';
import { ApiService, showToast } from './api.service';
import { TranslatePipe } from './i18n.service';

// ── Types ──────────────────────────────────────────────────────────────────────
export type FixtureCategory = 'sewer' | 'stormwater' | 'general';
export type PipeStyle = 'solid' | 'dashed' | 'dotted' | 'dash-dot';
export type ActiveTool = 'select' | 'draw-ss' | 'draw-sw' | 'draw-custom' | 'draw-building' | 'measure' | 'direction' | 'crop';

export interface Fixture {
  id: string; label: string; category: FixtureCategory; x: number; y: number;
}
export interface DrainPipe {
  id: string; type: 'sewer' | 'stormwater' | 'custom';
  nodes: { x: number; y: number }[];
  gradient?: string; diameter_mm?: number;
  color?: string; style?: PipeStyle; strokeWidth?: number; label?: string;
}
export interface Measurement {
  id: string; x1: number; y1: number; x2: number; y2: number; label: string;
  axis: 'h' | 'v' | 'free';
  manualLabel?: boolean;
}
export interface DirectionLine {
  id: string; x1: number; y1: number; x2: number; y2: number; label?: string;
}
interface RawFixture { id: string; label: string; pipe_type: string; coords: [number, number]; }
interface RawPipe { id: string; type: 'sewer' | 'stormwater'; path: [number, number][]; gradient?: string; diameter_mm?: number; }
interface RawExtracted { meta: { image_width_px: number; image_height_px: number }; building_outline: [number, number][]; pipes: RawPipe[]; fixtures: RawFixture[]; measurements?: any[]; directional_lines?: any[]; }

const CANVAS_W = 700, CANVAS_H = 1000, CANVAS_PADDING = 60, SNAP = 10;

@Component({
  selector: 'app-mobile-as-built-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, StageComponent, CoreShapeComponent, TranslatePipe],
  templateUrl: './mobile-as-built-editor.component.html',
  styleUrl: './mobile-as-built-editor.component.css',
})
export class MobileAsBuiltEditorComponent implements OnInit, OnDestroy {
  private api    = inject(ApiService);
  private http   = inject(HttpClient);
  private cdr    = inject(ChangeDetectorRef);
  private router = inject(Router);
  private route  = inject(ActivatedRoute);

  configStage = signal({ width: CANVAS_W, height: CANVAS_H });

  buildingOutline = signal<{ x: number; y: number }[]>([]);
  pipes           = signal<DrainPipe[]>([]);
  fixtures        = signal<Fixture[]>([]);
  measurements    = signal<Measurement[]>([]);
  directionalLines = signal<DirectionLine[]>([]);

  buildingConsent = ''; ownerName = ''; jobAddress = '';
  drainLayer = ''; regNumber = ''; dateSubmitted = ''; legalDescription = '';
  postcode = ''; lotNumber = ''; dpNumber = '';
  notes = '';
  drawingWidthMetres = signal(30);

  isExtracting = signal(false);
  isGenerating = signal(false);
  snapToGrid   = signal(true);
  selectedId   = signal<string | null>(null);
  activeTool   = signal<ActiveTool>('select');

  snapshotMode = signal(true); // Always false after we load but good for setup

  currentJobId = signal<string | null>(null);
  docStatus    = signal<string>('Needs Review');
  sourceImageUrl = signal<string | null>(null);
  isSaving     = signal(false);
  isSubmittingTraining = signal(false);
  trainingSessionId = signal<string | null>(null);
  trainingSampleId = signal<string | null>(null);

  customLineColor  = '#9333ea';
  customLineStyle: PipeStyle = 'dashed';
  customLineWidth  = 2;
  customLineLabel  = '';

  private drawPoints: { x: number; y: number }[] = [];
  previewLine   = signal<number[]>([]);
  measureStart: { x: number; y: number } | null = null;
  directionStart: { x: number; y: number } | null = null;
  cropRect = signal<{ x: number; y: number; width: number; height: number } | null>(null);
  private cropStart: { x: number; y: number } | null = null;

  // ── Undo / Redo ──────────────────────────────────────────────────────────────
  private undoStack: any[][] = [];
  private redoStack: any[][] = [];
  canUndo = signal(false);
  canRedo = signal(false);

  private snapshot(): void {
    this.undoStack.push([
      JSON.parse(JSON.stringify(this.buildingOutline())),
      JSON.parse(JSON.stringify(this.pipes())),
      JSON.parse(JSON.stringify(this.fixtures())),
      JSON.parse(JSON.stringify(this.measurements())),
      JSON.parse(JSON.stringify(this.directionalLines())),
    ]);
    this.redoStack = [];
    this.canUndo.set(this.undoStack.length > 0);
    this.canRedo.set(false);
  }

  undo(): void {
    if (!this.undoStack.length) return;
    this.redoStack.push([
      JSON.parse(JSON.stringify(this.buildingOutline())),
      JSON.parse(JSON.stringify(this.pipes())),
      JSON.parse(JSON.stringify(this.fixtures())),
      JSON.parse(JSON.stringify(this.measurements())),
      JSON.parse(JSON.stringify(this.directionalLines())),
    ]);
    const [o, p, f, m, d] = this.undoStack.pop()!;
    this.buildingOutline.set(o); this.pipes.set(p); this.fixtures.set(f); this.measurements.set(m); this.directionalLines.set(d ?? []);
    this.canUndo.set(this.undoStack.length > 0);
    this.canRedo.set(true);
  }

  redo(): void {
    if (!this.redoStack.length) return;
    this.undoStack.push([
      JSON.parse(JSON.stringify(this.buildingOutline())),
      JSON.parse(JSON.stringify(this.pipes())),
      JSON.parse(JSON.stringify(this.fixtures())),
      JSON.parse(JSON.stringify(this.measurements())),
      JSON.parse(JSON.stringify(this.directionalLines())),
    ]);
    const [o, p, f, m, d] = this.redoStack.pop()!;
    this.buildingOutline.set(o); this.pipes.set(p); this.fixtures.set(f); this.measurements.set(m); this.directionalLines.set(d ?? []);
    this.canUndo.set(true);
    this.canRedo.set(this.redoStack.length > 0);
  }

  readonly gridSize = SNAP;
  readonly CANVAS_W_ = CANVAS_W;
  readonly CANVAS_H_ = CANVAS_H;
  readonly gridRows = Array.from({ length: Math.floor(CANVAS_H / SNAP) }, (_, i) => i);
  readonly gridCols = Array.from({ length: Math.floor(CANVAS_W / SNAP) }, (_, i) => i);

  ssNodeCount  = computed(() => this.fixtures().filter(f => f.category === 'sewer').length);
  swNodeCount  = computed(() => this.fixtures().filter(f => f.category === 'stormwater').length);
  genNodeCount = computed(() => this.fixtures().filter(f => f.category === 'general').length);
  ssSegCount   = computed(() => this.pipes().filter(p => p.type === 'sewer').length);
  swSegCount   = computed(() => this.pipes().filter(p => p.type === 'stormwater').length);
  metresPerPixel = computed(() => this.drawingWidthMetres() / CANVAS_W);
  totalPipeLengthMetres = computed(() => this.round1(this.pipes().reduce((sum, pipe) => sum + this.polylineLength(pipe.nodes) * this.metresPerPixel(), 0)));
  buildingAreaSqm = computed(() => this.round1(Math.abs(this.polygonArea(this.buildingOutline())) * Math.pow(this.metresPerPixel(), 2)));
  selectedPipe = computed(() => this.pipes().find(pipe => pipe.id === this.selectedId()) ?? null);
  selectedMeasurement = computed(() => this.measurements().find(m => m.id === this.selectedId()) ?? null);
  selectedDirectionLine = computed(() => this.directionalLines().find(line => line.id === this.selectedId()) ?? null);
  isBuildingSelected = computed(() => this.selectedId() === 'building-outline' && this.buildingOutline().length > 0);
  selectedLineLengthMetres = computed(() => {
    const pipe = this.selectedPipe();
    if (pipe) return this.round1(this.polylineLength(pipe.nodes) * this.metresPerPixel());
    const measurement = this.selectedMeasurement();
    if (measurement) return this.round1(Math.hypot(measurement.x2 - measurement.x1, measurement.y2 - measurement.y1) * this.metresPerPixel());
    return 0;
  });

  /** True whenever a draw or measure tool is active — shapes should not intercept clicks. */
  isDrawingMode = computed(() => this.activeTool() !== 'select');

  buildingOutlinePoints = computed(() => this.flattenPoints(this.buildingOutline()));
  boundaryPointHandles = computed(() => {
    const points = this.buildingOutline();
    const lastIndex = points.length - 1;
    const isClosed = points.length > 2 && points[0]?.x === points[lastIndex]?.x && points[0]?.y === points[lastIndex]?.y;
    return points.map((point, index) => ({ ...point, index })).filter(handle => !isClosed || handle.index !== lastIndex);
  });

  readonly SEWER_FIXTURES   = ['WC','SH','HB','BT','KS','LDRY','TV','AAV','ORG','GT','SS_IP'];
  readonly SW_FIXTURES      = ['DP','IC','RWH','SW_IP'];
  readonly GENERAL_FIXTURES = ['IB','IJ','IP','SS CON','SW CON','R/I','SW TANK','SW PUMP','HWC'];

  readonly STYLE_OPTIONS: { value: PipeStyle; label: string }[] = [
    { value: 'solid',    label: '—— Solid' },
    { value: 'dashed',   label: '- - Dashed' },
    { value: 'dotted',   label: '··· Dotted' },
    { value: 'dash-dot', label: '-·- Dash-dot' },
  ];

  ngOnInit(): void {
    this.loadGlobalScale();
    this.route.queryParamMap.subscribe(params => {
      this.trainingSessionId.set(params.get('trainingSessionId'));
      this.trainingSampleId.set(params.get('trainingSampleId'));
    });
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.loadJob(id);
      } else {
        this.goBack();
      }
    });
  }
  
  ngOnDestroy(): void {}

  sourceImageHref(): string | null {
    const path = this.sourceImageUrl();
    if (!path) return null;
    return this.api.assetUrl(path);
  }

  goBack(): void {
    const currentId = this.currentJobId();
    if (currentId) {
      this.http.get<any>(`${this.api.baseUrl}/ai/status_drainage.php?job_id=${currentId}`, { headers: (this.api as any).getHeaders() }).subscribe({
        next: (res) => {
          const referenceId = res?.data?.reference_id;
          const modelType = res?.data?.model_type;
          if (referenceId && modelType === 'Project') {
            this.router.navigate(['/project', referenceId]);
          } else {
            this.router.navigate(['/']);
          }
        },
        error: () => this.router.navigate(['/'])
      });
      return;
    }
    this.router.navigate(['/']);
  }

  loadJob(jobId: string): void {
    this.http.get<any>(`${this.api.baseUrl}/ai/status_drainage.php?job_id=${jobId}`, { headers: (this.api as any).getHeaders() }).subscribe({
      next: (res) => {
        if (res?.data?.status === 'completed' && res.data.result) {
          const result = res.data.result;
          this.currentJobId.set(jobId);
          this.docStatus.set(res.data.doc_status || 'Needs Review');
          this.sourceImageUrl.set(res.data.source_image_url || null);
          this.notes = res.data.notes || '';
          if (result.editor_state) {
            this.applyEditorState(result.editor_state);
          } else if (result.extracted) {
            this.applyExtracted(result.extracted);
          } else {
            showToast('error', 'Invalid job payload'); return;
          }
          const dbWidth = Number(res.data.drawing_width_metres);
          if (Number.isFinite(dbWidth) && dbWidth > 0) {
            this.drawingWidthMetres.set(dbWidth);
          }
          showToast('success', 'Loaded extraction onto canvas');
        } else {
          showToast('error', 'Job data not available or still processing');
        }
      },
      error: (e) => showToast('error', 'Failed to load job')
    });
  }

  private applyEditorState(state: any): void {
    this.buildingOutline.set(state.buildingOutline ?? []);
    this.pipes.set(state.pipes ?? []);
    this.fixtures.set(state.fixtures ?? []);
    this.measurements.set(state.measurements ?? []);
    this.directionalLines.set(state.directionalLines ?? state.directional_lines ?? []);
    if (state.metadata) {
      this.buildingConsent = state.metadata.building_consent || '';
      this.ownerName = state.metadata.owner || '';
      this.jobAddress = state.metadata.address || '';
      this.drainLayer = state.metadata.drain_layer || '';
      this.regNumber = state.metadata.reg_number || '';
      this.dateSubmitted = state.metadata.date_submitted || '';
      this.legalDescription = state.metadata.legal_description || '';
      this.postcode = state.metadata.postcode || '';
      this.lotNumber = state.metadata.lot_number || '';
      this.dpNumber = state.metadata.dp_number || '';
      this.notes = state.metadata.notes || this.notes || '';
      const widthM = Number(state.metadata.drawing_width_metres);
      if (Number.isFinite(widthM) && widthM > 0) {
        this.drawingWidthMetres.set(widthM);
      }
    }
  }

  saveCurrentJob(silent = false): void {
    const id = this.currentJobId();
    if (!id) return;
    this.isSaving.set(true);
    const editorState = this.buildEditorState();
    this.http.post<any>(`${this.api.baseUrl}/ai/save_drainage_job.php`, { job_id: id, editor_state: editorState }, { headers: (this.api as any).getHeaders() }).subscribe({
      next: (res) => {
        this.isSaving.set(false);
        if (res?.success) {
          this.docStatus.set('Reviewed');
          if (!silent) showToast('success', 'Canvas saved successfully');
        }
        else if (!silent) showToast('error', res?.message || 'Failed to save');
      },
      error: (e) => { this.isSaving.set(false); if (!silent) showToast('error', 'Error saving canvas'); }
    });
  }

  submitTrainingExample(): void {
    const jobId = this.currentJobId();
    const sampleId = this.trainingSampleId();
    if (!jobId || !sampleId || this.isSubmittingTraining()) return;

    this.isSubmittingTraining.set(true);
    this.http.post<any>(`${this.api.baseUrl}/ai/save_drainage_job.php`, {
      job_id: jobId,
      editor_state: this.buildEditorState()
    }, { headers: (this.api as any).getHeaders() }).subscribe({
      next: (saveRes) => {
        if (!saveRes?.success) {
          this.isSubmittingTraining.set(false);
          showToast('error', saveRes?.message || 'Could not save corrected drawing');
          return;
        }

        this.http.post<any>(`${this.api.baseUrl}/ai/submit_as_built_training_sample.php`, {
          sample_id: Number(sampleId)
        }, { headers: (this.api as any).getHeaders() }).subscribe({
          next: (submitRes) => {
            this.isSubmittingTraining.set(false);
            if (submitRes?.success) {
              showToast('success', 'Training example submitted');
              this.goBack();
            } else {
              showToast('error', submitRes?.message || 'Could not submit training example');
            }
          },
          error: () => {
            this.isSubmittingTraining.set(false);
            showToast('error', 'Could not submit training example');
          }
        });
      },
      error: () => {
        this.isSubmittingTraining.set(false);
        showToast('error', 'Could not save corrected drawing');
      }
    });
  }

  private buildEditorState(): any {
    return {
      buildingOutline: this.buildingOutline(),
      pipes: this.pipes(),
      fixtures: this.fixtures(),
      measurements: this.measurements(),
      directionalLines: this.directionalLines(),
      metadata: { building_consent: this.buildingConsent, owner: this.ownerName, address: this.jobAddress, drain_layer: this.drainLayer, reg_number: this.regNumber, date_submitted: this.dateSubmitted, legal_description: this.legalDescription, postcode: this.postcode, lot_number: this.lotNumber, dp_number: this.dpNumber, drawing_width_metres: this.drawingWidthMetres(), notes: this.notes }
    };
  }

  private applyExtracted(raw: RawExtracted): void {
    const { tx, ty } = this.buildTransform(raw, raw.meta?.image_width_px ?? CANVAS_W, raw.meta?.image_height_px ?? CANVAS_H);
    this.buildingOutline.set((raw.building_outline ?? []).map(([px, py]) => ({ x: tx(px), y: ty(py) })));
    this.pipes.set((raw.pipes ?? []).map(p => ({
      id: p.id, type: p.type, gradient: p.gradient, diameter_mm: p.diameter_mm,
      nodes: (p.path ?? []).map(([px, py]) => ({ x: tx(px), y: ty(py) })),
    })));
    this.fixtures.set((raw.fixtures ?? []).map(f => ({
      id: f.id, label: f.label ?? 'UNKNOWN',
      category: this.inferCategory(f.label, f.pipe_type),
      x: tx(f.coords[0]), y: ty(f.coords[1]),
    })));
    this.measurements.set((raw.measurements ?? []).map((m: any, index: number) => {
      const points = m.points ?? [];
      const [x1, y1, x2, y2] = points.length >= 4 ? points : [
        m.start?.[0] ?? 0,
        m.start?.[1] ?? 0,
        m.end?.[0] ?? 0,
        m.end?.[1] ?? 0,
      ];
      return {
        id: String(m.id ?? `dim-ai-${index + 1}`),
        x1: tx(Number(x1) || 0),
        y1: ty(Number(y1) || 0),
        x2: tx(Number(x2) || 0),
        y2: ty(Number(y2) || 0),
        label: String(m.label ?? ''),
        axis: Math.abs((Number(x2) || 0) - (Number(x1) || 0)) > Math.abs((Number(y2) || 0) - (Number(y1) || 0)) ? 'h' : 'v',
      };
    }));
    this.directionalLines.set((raw.directional_lines ?? []).map((line: any, index: number) => {
      const points = line.points ?? [];
      const [x1, y1, x2, y2] = points.length >= 4 ? points : [
        line.start?.[0] ?? 0,
        line.start?.[1] ?? 0,
        line.end?.[0] ?? 0,
        line.end?.[1] ?? 0,
      ];
      return {
        id: String(line.id ?? `dir-ai-${index + 1}`),
        x1: tx(Number(x1) || 0),
        y1: ty(Number(y1) || 0),
        x2: tx(Number(x2) || 0),
        y2: ty(Number(y2) || 0),
        label: line.label ? String(line.label) : undefined,
      };
    }));
  }

  private inferCategory(label: string, pipeType: string): FixtureCategory {
    const up = (label ?? '').toUpperCase();
    if (this.GENERAL_FIXTURES.map(x => x.toUpperCase()).includes(up)) return 'general';
    if (pipeType === 'stormwater') return 'stormwater';
    return 'sewer';
  }

  private buildTransform(raw: RawExtracted, srcW: number, srcH: number) {
    const allX: number[] = [], allY: number[] = [];
    (raw.building_outline ?? []).forEach(([x, y]) => { allX.push(x); allY.push(y); });
    (raw.pipes ?? []).forEach(p => p.path.forEach(([x, y]) => { allX.push(x); allY.push(y); }));
    (raw.fixtures ?? []).forEach(f => { allX.push(f.coords[0]); allY.push(f.coords[1]); });
    (raw.measurements ?? []).forEach((m: any) => {
      const points = m.points ?? [];
      if (points.length >= 4) { allX.push(points[0], points[2]); allY.push(points[1], points[3]); }
    });
    (raw.directional_lines ?? []).forEach((line: any) => {
      const points = line.points ?? [];
      if (points.length >= 4) { allX.push(points[0], points[2]); allY.push(points[1], points[3]); }
    });
    if (!allX.length) return { tx: (px: number) => Math.round(px / srcW * CANVAS_W), ty: (py: number) => Math.round(py / srcH * CANVAS_H) };
    const minX = Math.min(...allX), maxX = Math.max(...allX), minY = Math.min(...allY), maxY = Math.max(...allY);
    const scale = Math.min((CANVAS_W - CANVAS_PADDING * 2) / (maxX - minX || 1), (CANVAS_H - CANVAS_PADDING * 2) / (maxY - minY || 1));
    const offsetX = CANVAS_PADDING + ((CANVAS_W - CANVAS_PADDING * 2) - (maxX - minX) * scale) / 2 - minX * scale;
    const offsetY = CANVAS_PADDING + ((CANVAS_H - CANVAS_PADDING * 2) - (maxY - minY) * scale) / 2 - minY * scale;
    return { tx: (px: number) => Math.round(px * scale + offsetX), ty: (py: number) => Math.round(py * scale + offsetY) };
  }

  // ── Tools ────────────────────────────────────────────────────────────────────
  setTool(tool: ActiveTool): void {
    this.activeTool.set(tool);
    this.drawPoints = []; this.previewLine.set([]);
    this.measureStart = null;
    this.directionStart = null;
    this.cropStart = null;
    if (tool !== 'crop') this.cropRect.set(null);
  }

  onStageClick(e: any): void {
    // Only used in select mode to deselect when clicking the background
    if (this.activeTool() !== 'select') return;
    const isStage = e.target && typeof e.target.getStage === 'function' && e.target === e.target.getStage();
    if (isStage) this.selectedId.set(null);
  }

  /** Native overlay click — used by draw-ss, draw-sw, draw-custom, draw-building, measure */
  onCanvasClick(e: MouseEvent): void {
    const tool = this.activeTool();
    if (tool === 'select' || tool === 'crop') return;
    const x = this.snapVal(e.offsetX);
    const y = this.snapVal(e.offsetY);
    if (tool === 'measure') { this.handleMeasureClick(x, y); return; }
    if (tool === 'direction') { this.handleDirectionClick(x, y); return; }
    this.drawPoints.push({ x, y });
    this.previewLine.set(this.flattenPoints(this.drawPoints));
  }

  onCanvasMouseMove(e: MouseEvent): void {
    const tool = this.activeTool();
    if (tool === 'select') return;
    const x = this.snapVal(e.offsetX);
    const y = this.snapVal(e.offsetY);
    if (tool === 'crop') {
      if (this.cropStart) this.updateCropRect(this.cropStart, { x, y });
      return;
    }
    if (tool === 'measure' && this.measureStart) {
      this.previewLine.set([this.measureStart.x, this.measureStart.y, x, y]);
    } else if (tool === 'direction' && this.directionStart) {
      this.previewLine.set([this.directionStart.x, this.directionStart.y, x, y]);
    } else if (this.drawPoints.length > 0) {
      this.previewLine.set([...this.flattenPoints(this.drawPoints), x, y]);
    }
  }

  onCanvasDblClick(e: MouseEvent): void {
    const tool = this.activeTool();
    if (!['draw-ss','draw-sw','draw-custom','draw-building'].includes(tool)) return;
    if (this.drawPoints.length < 2) { this.drawPoints = []; this.previewLine.set([]); return; }
    const points = [...this.drawPoints];
    this.drawPoints = []; this.previewLine.set([]);
    this.snapshot();
    if (tool === 'draw-building') { this.buildingOutline.set(points); return; }
    this.pipes.set([...this.pipes(), {
      id: `pipe-${Date.now()}`, type: tool === 'draw-ss' ? 'sewer' : tool === 'draw-sw' ? 'stormwater' : 'custom',
      nodes: points,
      color: tool === 'draw-custom' ? this.customLineColor : undefined,
      style: tool === 'draw-custom' ? this.customLineStyle : undefined,
      strokeWidth: tool === 'draw-custom' ? this.customLineWidth : undefined,
      label: tool === 'draw-custom' ? (this.customLineLabel || undefined) : undefined,
    }]);
  }

  onCanvasMouseDown(e: MouseEvent): void {
    if (this.activeTool() !== 'crop') return;
    const x = this.snapVal(e.offsetX);
    const y = this.snapVal(e.offsetY);
    this.cropStart = { x, y };
    this.cropRect.set({ x, y, width: 0, height: 0 });
  }

  onCanvasMouseUp(e: MouseEvent): void {
    if (this.activeTool() !== 'crop' || !this.cropStart) return;
    const x = this.snapVal(e.offsetX);
    const y = this.snapVal(e.offsetY);
    this.updateCropRect(this.cropStart, { x, y });
    this.cropStart = null;
  }

  onStageDblClick(e: any): void {
    // Double-click on stage background in select mode — no-op
    // Actual drawing dblclick is handled by onCanvasDblClick overlay
  }

  onStageMouseMove(e: any): void {
    // Preview line is now handled by the native onCanvasMouseMove overlay
  }

  private handleMeasureClick(x: number, y: number): void {
    if (!this.measureStart) {
      this.measureStart = { x, y };
    } else {
      const dx = x - this.measureStart.x, dy = y - this.measureStart.y;
      const px = Math.sqrt(dx * dx + dy * dy);
      const metres = this.formatMetres(px * this.metresPerPixel());
      this.snapshot();
      this.measurements.set([...this.measurements(), {
        id: `dim-${Date.now()}`, x1: this.measureStart.x, y1: this.measureStart.y, x2: x, y2: y,
        label: metres, axis: Math.abs(dx) > Math.abs(dy) ? 'h' : 'v',
      }]);
      this.measureStart = null; this.previewLine.set([]);
    }
  }

  private handleDirectionClick(x: number, y: number): void {
    if (!this.directionStart) {
      this.directionStart = { x, y };
      return;
    }
    this.snapshot();
    this.directionalLines.set([...this.directionalLines(), {
      id: `dir-${Date.now()}`,
      x1: this.directionStart.x,
      y1: this.directionStart.y,
      x2: x,
      y2: y,
    }]);
    this.directionStart = null;
    this.previewLine.set([]);
  }

  // ── Fixtures ─────────────────────────────────────────────────────────────────
  addFixture(label: string): void {
    const category: FixtureCategory = this.SEWER_FIXTURES.includes(label) ? 'sewer'
      : this.SW_FIXTURES.includes(label) ? 'stormwater' : 'general';
    const fix: Fixture = { id: `fix-${Date.now()}`, label, category, x: 120, y: 120 };
    this.snapshot();
    this.fixtures.set([...this.fixtures(), fix]);
    this.selectedId.set(fix.id);
  }

  // ── DRAG FIX: read group.x()/y() AFTER drag, snap, write back ──────────────
  onFixtureDragEnd(e: any, fixtureId: string): void {
    const group = e.target;
    if (!group || typeof group.x !== 'function') return;
    const dx = group.x(), dy = group.y();
    group.x(0); group.y(0);
    this.snapshot();
    this.fixtures.update(list => list.map(f => {
      if (f.id !== fixtureId) return f;
      return { ...f, x: this.snapVal((f.x ?? 0) + dx), y: this.snapVal((f.y ?? 0) + dy) };
    }));
  }

  onPipeDragEnd(e: any, pipeId: string): void {
    const shape = e.target;
    const dx = shape.x(), dy = shape.y();
    shape.x(0); shape.y(0);
    this.snapshot();
    this.pipes.update(list => list.map(p => 
      p.id === pipeId ? { ...p, nodes: p.nodes.map(n => ({ x: this.snapVal(n.x + dx), y: this.snapVal(n.y + dy) })) } : p
    ));
  }

  onMeasureDragEnd(e: any, mId: string): void {
    const group = e.target;
    const dx = group.x(), dy = group.y();
    group.x(0); group.y(0);
    this.snapshot();
    this.measurements.update(list => list.map(m =>
      m.id === mId ? { ...m, x1: m.x1+dx, y1: m.y1+dy, x2: m.x2+dx, y2: m.y2+dy } : m
    ));
  }

  onDirectionDragEnd(e: any, lineId: string): void {
    const group = e.target;
    const dx = group.x(), dy = group.y();
    group.x(0); group.y(0);
    this.snapshot();
    this.directionalLines.update(list => list.map(line =>
      line.id === lineId ? { ...line, x1: this.snapVal(line.x1 + dx), y1: this.snapVal(line.y1 + dy), x2: this.snapVal(line.x2 + dx), y2: this.snapVal(line.y2 + dy) } : line
    ));
  }

  onBoundaryPointDragEnd(e: any, pointIndex: number): void {
    const node = e.target;
    const x = this.snapVal(node.x());
    const y = this.snapVal(node.y());
    node.x(x); node.y(y);
    this.snapshot();
    this.buildingOutline.update(points => {
      const next = points.map(point => ({ ...point }));
      const lastIndex = next.length - 1;
      const wasClosed = lastIndex > 0 && points[0]?.x === points[lastIndex]?.x && points[0]?.y === points[lastIndex]?.y;
      next[pointIndex] = { x, y };
      if (pointIndex === 0 && wasClosed) {
        next[lastIndex] = { x, y };
      }
      return next;
    });
  }

  selectItem(id: string): void {
    this.selectedId.set(this.selectedId() === id ? null : id);
  }

  deleteSelected(): void {
    const id = this.selectedId(); if (!id) return;
    this.snapshot();
    this.fixtures.update(l => l.filter(f => f.id !== id));
    this.pipes.update(l => l.filter(p => p.id !== id));
    this.measurements.update(l => l.filter(m => m.id !== id));
    this.directionalLines.update(l => l.filter(line => line.id !== id));
    if (id === 'building-outline') this.buildingOutline.set([]);
    this.selectedId.set(null);
  }

  updateMeasureLabel(id: string, label: string): void {
    this.snapshot();
    this.measurements.update(l => l.map(m => m.id === id ? { ...m, label, manualLabel: true } : m));
  }

  updateDirectionLabel(id: string, label: string): void {
    this.snapshot();
    this.directionalLines.update(list => list.map(line => line.id === id ? { ...line, label: label.trim() || undefined } : line));
  }

  // ── PDF ──────────────────────────────────────────────────────────────────────
  generatePdf(): void {
    const missing = this.missingPdfFields();
    if (missing.length) {
      showToast('error', `Complete required fields first: ${missing.join(', ')}`);
      return;
    }
    this.isGenerating.set(true);
    const payload = {
      job_id: this.currentJobId(),
      orientation: 'portrait',
      metadata: { building_consent: this.buildingConsent, owner: this.ownerName, address: this.jobAddress, drain_layer: this.drainLayer, reg_number: this.regNumber, date_submitted: this.dateSubmitted, legal_description: this.legalDescription, postcode: this.postcode, lot_number: this.lotNumber, dp_number: this.dpNumber, drawing_width_metres: this.drawingWidthMetres(), notes: this.notes },
      canvas: { width: CANVAS_W, height: CANVAS_H },
      building_outline: this.buildingOutline().map(p => [p.x, p.y]),
      pipes: this.pipes().map(p => ({ id: p.id, type: p.type, path: p.nodes.map(n => [n.x, n.y]), gradient: p.gradient, diameter_mm: p.diameter_mm, color: p.color, style: p.style })),
      fixtures: this.fixtures().map(f => ({ id: f.id, label: f.label, category: f.category, coords: [f.x, f.y] })),
      measurements: this.measurements().map(m => ({ id: m.id, points: [m.x1,m.y1,m.x2,m.y2], label: m.label })),
      directional_lines: this.directionalLines().map(line => ({ id: line.id, points: [line.x1, line.y1, line.x2, line.y2], label: line.label })),
    };
    this.http.post<any>(`${this.api.baseUrl}/ai/generate_as_built_pdf.php`, payload, { headers: (this.api as any).getHeaders() }).subscribe({
      next: (res) => {
        this.isGenerating.set(false);
        if (res?.success) {
          const l = document.createElement('a');
          l.href = `data:application/pdf;base64,${res.data.pdf_base64}`;
          l.download = res.data.filename ?? 'as-built.pdf';
          l.click();
          this.docStatus.set('PDF Generated');
          showToast('success', 'PDF generated');
        }
      },
      error: (e) => { 
        this.isGenerating.set(false); 
        console.error(e);
        if (e.status === 524) {
          alert('Error 524: A timeout occurred. The server took too long to respond.');
        } else {
          showToast('error', 'PDF failed'); 
        }
      },
    });
  }

  // ── Konva config helpers ─────────────────────────────────────────────────────
  private missingPdfFields(): string[] {
    const fields = [
      ['Consent #', this.buildingConsent],
      ['Owner', this.ownerName],
      ['Address', this.jobAddress],
      ['Postcode', this.postcode],
      ['Legal description', this.legalDescription],
      ['Lot number', this.lotNumber],
      ['DP number', this.dpNumber],
      ['Drain-layer', this.drainLayer],
      ['Reg. no.', this.regNumber],
      ['Date', this.dateSubmitted],
    ];
    return fields.filter(([, value]) => !String(value || '').trim()).map(([label]) => label);
  }

  setDrawingWidthMetres(value: string | number): void {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      this.drawingWidthMetres.set(parsed);
    }
  }

  saveScaleAsDefault(): void {
    localStorage.setItem('asBuiltDefaultDrawingWidthMetres', String(this.drawingWidthMetres()));
    this.api.updateCompanySettings({
      as_built_default_drawing_width_metres: String(this.drawingWidthMetres())
    }).subscribe({
      next: (res: any) => {
        if (res?.success) showToast('success', 'Scale saved as global default');
        else showToast('error', res?.message || 'Could not save global scale');
      },
      error: () => showToast('error', 'Could not save global scale')
    });
  }

  recalculateMeasurements(): void {
    if (!this.measurements().length) return;
    this.snapshot();
    this.updateMeasurementLabels();
    showToast('success', 'Measurement labels recalculated');
  }

  hasDrawingContent(): boolean {
    return this.buildingOutline().length > 0 || this.pipes().length > 0 || this.fixtures().length > 0 || this.measurements().length > 0 || this.directionalLines().length > 0;
  }

  fitDrawingToCanvas(): void {
    const bounds = this.getDrawingBounds();
    if (!bounds) {
      showToast('error', 'No drawing content to fit');
      return;
    }

    const contentW = Math.max(1, bounds.maxX - bounds.minX);
    const contentH = Math.max(1, bounds.maxY - bounds.minY);
    const padding = 50;
    const scale = Math.min((CANVAS_W - padding * 2) / contentW, (CANVAS_H - padding * 2) / contentH);

    if (!Number.isFinite(scale) || scale <= 0) {
      showToast('error', 'Could not calculate drawing bounds');
      return;
    }

    const offsetX = padding + ((CANVAS_W - padding * 2) - contentW * scale) / 2 - bounds.minX * scale;
    const offsetY = padding + ((CANVAS_H - padding * 2) - contentH * scale) / 2 - bounds.minY * scale;
    const tx = (point: { x: number; y: number }) => ({
      x: this.snapVal(point.x * scale + offsetX),
      y: this.snapVal(point.y * scale + offsetY),
    });

    this.snapshot();
    this.buildingOutline.update(points => points.map(tx));
    this.pipes.update(list => list.map(pipe => ({ ...pipe, nodes: pipe.nodes.map(tx) })));
    this.fixtures.update(list => list.map(fixture => {
      const next = tx(fixture);
      return { ...fixture, x: next.x, y: next.y };
    }));
    this.measurements.update(list => list.map(m => {
      const start = tx({ x: m.x1, y: m.y1 });
      const end = tx({ x: m.x2, y: m.y2 });
      return { ...m, x1: start.x, y1: start.y, x2: end.x, y2: end.y };
    }));
    this.directionalLines.update(list => list.map(line => {
      const start = tx({ x: line.x1, y: line.y1 });
      const end = tx({ x: line.x2, y: line.y2 });
      return { ...line, x1: start.x, y1: start.y, x2: end.x, y2: end.y };
    }));

    // Scaling geometry changes pixels-per-metre. Adjust the drawing scale so real measurements remain stable.
    this.drawingWidthMetres.set(this.round1(this.drawingWidthMetres() / scale));
    this.updateMeasurementLabels();
    if (this.currentJobId()) this.saveCurrentJob(true);
    showToast('success', 'Blank drawing space cropped');
  }

  applyManualCrop(): void {
    const rect = this.cropRect();
    if (!rect || rect.width < 20 || rect.height < 20) {
      showToast('error', 'Draw a larger crop area first');
      return;
    }

    this.applyCropBounds({
      minX: rect.x,
      minY: rect.y,
      maxX: rect.x + rect.width,
      maxY: rect.y + rect.height,
    }, 'Manual crop applied');
    this.cropRect.set(null);
    this.cropStart = null;
    this.setTool('select');
  }

  rotateSelectedDrawing(degrees: -90 | 90 | 180): void {
    const rect = this.cropRect();
    if (!rect || rect.width < 20 || rect.height < 20) {
      showToast('error', 'Draw a crop box around the drawing first');
      return;
    }

    const inSelection = (point: { x: number; y: number }) =>
      point.x >= rect.x && point.x <= rect.x + rect.width && point.y >= rect.y && point.y <= rect.y + rect.height;

    const rotateOutline = this.buildingOutline().some(inSelection);
    const pipeIds = new Set(this.pipes().filter(pipe => pipe.nodes.some(inSelection)).map(pipe => pipe.id));
    const fixtureIds = new Set(this.fixtures().filter(fixture => inSelection(fixture)).map(fixture => fixture.id));
    const measurementIds = new Set(this.measurements().filter(m =>
      inSelection({ x: m.x1, y: m.y1 }) || inSelection({ x: m.x2, y: m.y2 })
    ).map(m => m.id));
    const directionIds = new Set(this.directionalLines().filter(line =>
      inSelection({ x: line.x1, y: line.y1 }) || inSelection({ x: line.x2, y: line.y2 })
    ).map(line => line.id));

    if (!rotateOutline && pipeIds.size === 0 && fixtureIds.size === 0 && measurementIds.size === 0 && directionIds.size === 0) {
      showToast('error', 'No drawing content found inside the selected box');
      return;
    }

    const cx = rect.x + rect.width / 2;
    const cy = rect.y + rect.height / 2;
    const radians = degrees * Math.PI / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const rotateRaw = (point: { x: number; y: number }) => {
      const dx = point.x - cx;
      const dy = point.y - cy;
      return {
        x: cx + dx * cos - dy * sin,
        y: cy + dx * sin + dy * cos,
      };
    };

    const selectedPoints: { x: number; y: number }[] = [];
    if (rotateOutline) selectedPoints.push(...this.buildingOutline());
    this.pipes().filter(pipe => pipeIds.has(pipe.id)).forEach(pipe => selectedPoints.push(...pipe.nodes));
    this.fixtures().filter(fixture => fixtureIds.has(fixture.id)).forEach(fixture => selectedPoints.push({ x: fixture.x, y: fixture.y }));
    this.measurements().filter(m => measurementIds.has(m.id)).forEach(m => {
      selectedPoints.push({ x: m.x1, y: m.y1 }, { x: m.x2, y: m.y2 });
    });
    this.directionalLines().filter(line => directionIds.has(line.id)).forEach(line => {
      selectedPoints.push({ x: line.x1, y: line.y1 }, { x: line.x2, y: line.y2 });
    });

    const rotatedBounds = this.boundsFromPoints(selectedPoints.map(rotateRaw));
    const margin = 20;
    let offsetX = 0;
    let offsetY = 0;

    if (rotatedBounds.maxX - rotatedBounds.minX <= CANVAS_W - margin * 2) {
      if (rotatedBounds.minX < margin) offsetX = margin - rotatedBounds.minX;
      else if (rotatedBounds.maxX > CANVAS_W - margin) offsetX = CANVAS_W - margin - rotatedBounds.maxX;
    } else {
      offsetX = CANVAS_W / 2 - (rotatedBounds.minX + rotatedBounds.maxX) / 2;
    }

    if (rotatedBounds.maxY - rotatedBounds.minY <= CANVAS_H - margin * 2) {
      if (rotatedBounds.minY < margin) offsetY = margin - rotatedBounds.minY;
      else if (rotatedBounds.maxY > CANVAS_H - margin) offsetY = CANVAS_H - margin - rotatedBounds.maxY;
    } else {
      offsetY = CANVAS_H / 2 - (rotatedBounds.minY + rotatedBounds.maxY) / 2;
    }

    const transform = (point: { x: number; y: number }) => {
      const rotated = rotateRaw(point);
      return {
        x: this.snapVal(rotated.x + offsetX),
        y: this.snapVal(rotated.y + offsetY),
      };
    };

    this.snapshot();
    if (rotateOutline) this.buildingOutline.update(points => points.map(transform));
    this.pipes.update(list => list.map(pipe => pipeIds.has(pipe.id) ? { ...pipe, nodes: pipe.nodes.map(transform) } : pipe));
    this.fixtures.update(list => list.map(fixture => {
      if (!fixtureIds.has(fixture.id)) return fixture;
      const next = transform(fixture);
      return { ...fixture, x: next.x, y: next.y };
    }));
    this.measurements.update(list => list.map(m => {
      if (!measurementIds.has(m.id)) return m;
      const start = transform({ x: m.x1, y: m.y1 });
      const end = transform({ x: m.x2, y: m.y2 });
      return { ...m, x1: start.x, y1: start.y, x2: end.x, y2: end.y };
    }));
    this.directionalLines.update(list => list.map(line => {
      if (!directionIds.has(line.id)) return line;
      const start = transform({ x: line.x1, y: line.y1 });
      const end = transform({ x: line.x2, y: line.y2 });
      return { ...line, x1: start.x, y1: start.y, x2: end.x, y2: end.y };
    }));

    const nextBounds = this.boundsFromPoints(selectedPoints.map(transform));
    this.cropRect.set({
      x: Math.max(0, Math.floor(nextBounds.minX)),
      y: Math.max(0, Math.floor(nextBounds.minY)),
      width: Math.min(CANVAS_W, Math.ceil(nextBounds.maxX)) - Math.max(0, Math.floor(nextBounds.minX)),
      height: Math.min(CANVAS_H, Math.ceil(nextBounds.maxY)) - Math.max(0, Math.floor(nextBounds.minY)),
    });
    if (this.currentJobId()) this.saveCurrentJob(true);
    showToast('success', `Selected drawing rotated ${Math.abs(degrees)} degrees`);
  }

  clearManualCrop(): void {
    this.cropRect.set(null);
    this.cropStart = null;
  }

  setSelectedLineLength(value: string | number): void {
    const metres = Number(value);
    if (!Number.isFinite(metres) || metres <= 0) {
      showToast('error', 'Enter a valid length');
      return;
    }

    const targetPx = metres / this.metresPerPixel();
    const pipe = this.selectedPipe();
    const measurement = this.selectedMeasurement();

    if (pipe) {
      const currentPx = this.polylineLength(pipe.nodes);
      if (currentPx <= 0 || pipe.nodes.length < 2) return;
      const anchor = pipe.nodes[0];
      const ratio = targetPx / currentPx;
      this.snapshot();
      this.pipes.update(list => list.map(item => {
        if (item.id !== pipe.id) return item;
        return {
          ...item,
          nodes: item.nodes.map((node, index) => index === 0 ? node : {
            x: this.snapVal(anchor.x + (node.x - anchor.x) * ratio),
            y: this.snapVal(anchor.y + (node.y - anchor.y) * ratio),
          })
        };
      }));
      showToast('success', 'Pipe length updated');
      return;
    }

    if (measurement) {
      showToast('error', 'Measurement corrections are text-only. Edit the manual text field instead.');
      return;
    }

    showToast('error', 'Select a pipe first');
  }

  private loadGlobalScale(): void {
    const saved = Number(localStorage.getItem('asBuiltDefaultDrawingWidthMetres'));
    if (Number.isFinite(saved) && saved > 0) {
      this.drawingWidthMetres.set(saved);
    }
    this.api.getCompanySettings().subscribe({
      next: (res: any) => {
        const dbDefault = Number(res?.data?.as_built_default_drawing_width_metres);
        if (Number.isFinite(dbDefault) && dbDefault > 0) {
          this.drawingWidthMetres.set(dbDefault);
          localStorage.setItem('asBuiltDefaultDrawingWidthMetres', String(dbDefault));
        }
      }
    });
  }

  private formatMetres(metres: number): string {
    return `${this.round1(metres)}M`;
  }

  private updateMeasurementLabels(): void {
    this.measurements.update(list => list.map(m => {
      if (m.manualLabel) return m;
      const px = Math.hypot(m.x2 - m.x1, m.y2 - m.y1);
      return { ...m, label: this.formatMetres(px * this.metresPerPixel()) };
    }));
  }

  private updateCropRect(start: { x: number; y: number }, end: { x: number; y: number }): void {
    this.cropRect.set({
      x: Math.max(0, Math.min(start.x, end.x)),
      y: Math.max(0, Math.min(start.y, end.y)),
      width: Math.min(CANVAS_W, Math.max(start.x, end.x)) - Math.max(0, Math.min(start.x, end.x)),
      height: Math.min(CANVAS_H, Math.max(start.y, end.y)) - Math.max(0, Math.min(start.y, end.y)),
    });
  }

  private applyCropBounds(bounds: { minX: number; minY: number; maxX: number; maxY: number }, successMessage: string): void {
    const contentW = Math.max(1, bounds.maxX - bounds.minX);
    const contentH = Math.max(1, bounds.maxY - bounds.minY);
    const padding = 50;
    const scale = Math.min((CANVAS_W - padding * 2) / contentW, (CANVAS_H - padding * 2) / contentH);

    if (!Number.isFinite(scale) || scale <= 0) {
      showToast('error', 'Could not calculate crop bounds');
      return;
    }

    const offsetX = padding + ((CANVAS_W - padding * 2) - contentW * scale) / 2 - bounds.minX * scale;
    const offsetY = padding + ((CANVAS_H - padding * 2) - contentH * scale) / 2 - bounds.minY * scale;
    const tx = (point: { x: number; y: number }) => ({
      x: this.snapVal(point.x * scale + offsetX),
      y: this.snapVal(point.y * scale + offsetY),
    });

    this.snapshot();
    this.buildingOutline.update(points => points.map(tx));
    this.pipes.update(list => list.map(pipe => ({ ...pipe, nodes: pipe.nodes.map(tx) })));
    this.fixtures.update(list => list.map(fixture => {
      const next = tx(fixture);
      return { ...fixture, x: next.x, y: next.y };
    }));
    this.measurements.update(list => list.map(m => {
      const start = tx({ x: m.x1, y: m.y1 });
      const end = tx({ x: m.x2, y: m.y2 });
      return { ...m, x1: start.x, y1: start.y, x2: end.x, y2: end.y };
    }));
    this.directionalLines.update(list => list.map(line => {
      const start = tx({ x: line.x1, y: line.y1 });
      const end = tx({ x: line.x2, y: line.y2 });
      return { ...line, x1: start.x, y1: start.y, x2: end.x, y2: end.y };
    }));

    this.drawingWidthMetres.set(this.round1(this.drawingWidthMetres() / scale));
    this.updateMeasurementLabels();
    if (this.currentJobId()) this.saveCurrentJob(true);
    showToast('success', successMessage);
  }

  private round1(value: number): number {
    return Math.round(value * 10) / 10;
  }

  private polylineLength(points: { x: number; y: number }[]): number {
    return points.reduce((sum, point, index) => {
      if (index === 0) return sum;
      const prev = points[index - 1];
      return sum + Math.hypot(point.x - prev.x, point.y - prev.y);
    }, 0);
  }

  private polygonArea(points: { x: number; y: number }[]): number {
    if (points.length < 3) return 0;
    return points.reduce((sum, point, index) => {
      const next = points[(index + 1) % points.length];
      return sum + point.x * next.y - next.x * point.y;
    }, 0) / 2;
  }

  private getDrawingBounds(): { minX: number; minY: number; maxX: number; maxY: number } | null {
    const xs: number[] = [];
    const ys: number[] = [];
    const add = (x: number, y: number) => {
      if (Number.isFinite(x) && Number.isFinite(y)) {
        xs.push(x);
        ys.push(y);
      }
    };

    this.buildingOutline().forEach(p => add(p.x, p.y));
    this.pipes().forEach(pipe => pipe.nodes.forEach(p => add(p.x, p.y)));
    this.fixtures().forEach(f => add(f.x, f.y));
    this.measurements().forEach(m => {
      add(m.x1, m.y1);
      add(m.x2, m.y2);
    });
    this.directionalLines().forEach(line => {
      add(line.x1, line.y1);
      add(line.x2, line.y2);
    });

    if (!xs.length || !ys.length) return null;
    return {
      minX: Math.min(...xs),
      minY: Math.min(...ys),
      maxX: Math.max(...xs),
      maxY: Math.max(...ys),
    };
  }

  private boundsFromPoints(points: { x: number; y: number }[]): { minX: number; minY: number; maxX: number; maxY: number } {
    const xs = points.map(point => point.x).filter(Number.isFinite);
    const ys = points.map(point => point.y).filter(Number.isFinite);
    if (!xs.length || !ys.length) {
      return { minX: 0, minY: 0, maxX: CANVAS_W, maxY: CANVAS_H };
    }
    return {
      minX: Math.min(...xs),
      minY: Math.min(...ys),
      maxX: Math.max(...xs),
      maxY: Math.max(...ys),
    };
  }

  private snapVal(v: number): number { return this.snapToGrid() ? Math.round(v / SNAP) * SNAP : Math.round(v); }

  flattenPoints(nodes: { x: number; y: number }[]): number[] {
    return nodes.reduce<number[]>((a, n) => [...a, n.x, n.y], []);
  }

  getPipeConfig(pipe: DrainPipe): object {
    let stroke = '#cc2200', dash: number[] = [6, 4], width = pipe.strokeWidth ?? 2;
    if (pipe.type === 'stormwater') { stroke = '#0055cc'; dash = [10, 5]; }
    else if (pipe.type === 'custom') { stroke = pipe.color ?? '#9333ea'; dash = this.styleToDash(pipe.style ?? 'dashed'); }
    const listening = !this.isDrawingMode();
    return { points: this.flattenPoints(pipe.nodes), stroke, strokeWidth: width, dash, lineCap: 'round', lineJoin: 'round', hitStrokeWidth: 14, draggable: listening, listening };
  }

  private styleToDash(s: PipeStyle): number[] {
    return s === 'solid' ? [] : s === 'dotted' ? [2, 4] : s === 'dash-dot' ? [8, 4, 2, 4] : [8, 5];
  }

  fixtureCategoryColor(cat: FixtureCategory): string {
    return cat === 'sewer' ? '#cc2200' : cat === 'stormwater' ? '#0055cc' : '#1a1a1a';
  }

  getFixtureGroupConfig(fix: Fixture): object {
    const listening = !this.isDrawingMode();
    return { name: fix.id, draggable: listening, listening };
  }

  getFixtureCircleConfig(fix: Fixture): object {
    const color = this.fixtureCategoryColor(fix.category);
    const sel   = this.selectedId() === fix.id;
    return { x: fix.x, y: fix.y, radius: 14, fill: 'white', stroke: sel ? '#f5a500' : color, strokeWidth: sel ? 3 : 2 };
  }

  getFixtureTextConfig(fix: Fixture): object {
    const color = this.fixtureCategoryColor(fix.category);
    const label = fix.label ?? '';
    const long  = label.length > 4;
    return { text: label, fontSize: long ? 7 : label.length > 2 ? 8 : 10, fontStyle: 'bold', fill: color, x: fix.x + (long ? -18 : label.length > 2 ? -11 : -7), y: fix.y - 5 };
  }

  getMeasureLineConfig(m: Measurement): object {
    return { points: [m.x1, m.y1, m.x2, m.y2], stroke: '#555', strokeWidth: 1, dash: [4, 3] };
  }

  getMeasureLabelBgConfig(m: Measurement): object {
    return { x: (m.x1+m.x2)/2-14, y: (m.y1+m.y2)/2-11, width: 28, height: 14, fill: 'white', stroke: '#bbb', strokeWidth: 0.5, cornerRadius: 2 };
  }

  getMeasureLabelConfig(m: Measurement): object {
    return { text: m.label, x: (m.x1+m.x2)/2-12, y: (m.y1+m.y2)/2-9, fontSize: 10, fontStyle: 'bold', fill: '#333' };
  }

  getTickConfig(mx: number, my: number, axis: string): object {
    const len = 6, pts = axis === 'v' ? [mx-len, my, mx+len, my] : [mx, my-len, mx, my+len];
    return { points: pts, stroke: '#555', strokeWidth: 1 };
  }

  getMeasureGroupConfig(m: Measurement): object {
    const listening = !this.isDrawingMode();
    return { name: m.id, draggable: listening, listening };
  }

  getDirectionGroupConfig(line: DirectionLine): object {
    const listening = !this.isDrawingMode();
    return { name: line.id, draggable: listening, listening };
  }

  getDirectionLineConfig(line: DirectionLine): object {
    return {
      points: [line.x1, line.y1, line.x2, line.y2],
      stroke: '#dc2626',
      strokeWidth: this.selectedId() === line.id ? 5 : 4,
      lineCap: 'round',
      hitStrokeWidth: 18,
      listening: false,
    };
  }

  getDirectionDotConfig(line: DirectionLine): object {
    return { x: line.x2, y: line.y2, radius: 8, fill: '#dc2626', stroke: '#ffffff', strokeWidth: 1.5, listening: false };
  }

  directionArrowheads(line: DirectionLine): number[][] {
    const dx = line.x2 - line.x1;
    const dy = line.y2 - line.y1;
    const length = Math.hypot(dx, dy);
    if (length < 35) return [];
    const ux = dx / length;
    const uy = dy / length;
    const arrowCount = Math.max(1, Math.min(4, Math.floor(length / 140)));
    const first = length / (arrowCount + 1);
    const size = 18;
    const spread = 10;
    return Array.from({ length: arrowCount }, (_, index) => {
      const dist = first * (index + 1);
      const x = line.x1 + ux * dist;
      const y = line.y1 + uy * dist;
      return [
        x - ux * size - uy * spread, y - uy * size + ux * spread,
        x, y,
        x - ux * size + uy * spread, y - uy * size - ux * spread,
      ];
    });
  }

  getDirectionArrowConfig(points: number[]): object {
    return { points, stroke: '#dc2626', strokeWidth: 4, lineCap: 'round', lineJoin: 'round', listening: false };
  }

  getDirectionLabelBgConfig(line: DirectionLine): object {
    const label = line.label ?? '';
    return { x: (line.x1 + line.x2) / 2 - Math.max(18, label.length * 4), y: (line.y1 + line.y2) / 2 - 13, width: Math.max(36, label.length * 8), height: 18, fill: 'white', stroke: '#fecaca', strokeWidth: 1, cornerRadius: 4, listening: false };
  }

  getDirectionLabelConfig(line: DirectionLine): object {
    const label = line.label ?? '';
    return { text: label, x: (line.x1 + line.x2) / 2 - Math.max(15, label.length * 3.5), y: (line.y1 + line.y2) / 2 - 9, fontSize: 11, fontStyle: 'bold', fill: '#b91c1c', listening: false };
  }

  getBoundaryHandleConfig(handle: { x: number; y: number; index: number }): object {
    return {
      x: handle.x,
      y: handle.y,
      radius: 7,
      fill: '#ffffff',
      stroke: '#f59e0b',
      strokeWidth: 2,
      draggable: true,
      listening: true,
    };
  }

  getPreviewLineConfig(): object {
    const t = this.activeTool();
    const color = t === 'draw-ss' ? '#cc220099' : t === 'draw-sw' ? '#0055cc99' : t === 'measure' ? '#55555599' : t === 'direction' ? '#dc262699' : t === 'draw-building' ? '#1a1a1a99' : (this.customLineColor + '99');
    return { points: this.previewLine(), stroke: color, strokeWidth: 1.5, dash: [4, 4], listening: false };
  }

  getMeasureStartConfig(): object {
    const point = this.measureStart ?? { x: 0, y: 0 };
    return { x: point.x, y: point.y, radius: 5, fill: '#fbbf24', stroke: '#d97706', strokeWidth: 1.5, listening: false };
  }

  getDirectionStartConfig(): object {
    const point = this.directionStart ?? { x: 0, y: 0 };
    return { x: point.x, y: point.y, radius: 5, fill: '#ef4444', stroke: '#b91c1c', strokeWidth: 1.5, listening: false };
  }

  getCropRectConfig(): object {
    const rect = this.cropRect();
    return {
      x: rect?.x ?? 0,
      y: rect?.y ?? 0,
      width: rect?.width ?? 0,
      height: rect?.height ?? 0,
      stroke: '#0ea5e9',
      strokeWidth: 2,
      dash: [8, 5],
      fill: 'rgba(14,165,233,0.08)',
      listening: false,
    };
  }
}
